<div class="personal-tracking-container">
  <div class="header">
    <h2>Personal Event Tracking</h2>
    <div class="action-buttons mt-2">
      <button class="btn btn-outline-info me-2" (click)="refreshEvents()" title="Sync employee birthdays and hire dates">
        <i class="fa fa-sync"></i> Sync Employee Data
      </button>
      <button class="btn btn-primary" (click)="openAddEventModal()">
        Add New Event
      </button>
    </div>
  </div>

  <div class="row">
    <!-- Calendar View -->
    <div class="col-md-8">
      <div class="card calendar-card">
        <div class="card-header">
          <div class="calendar-controls">
            <button class="btn btn-sm btn-outline-secondary" (click)="previousMonth()" aria-label="Previous Month">
              <fa-icon [icon]="faChevronLeft"></fa-icon>
            </button>
            <h5 class="month-title">
              {{ monthNames[currentMonthDate.getMonth()] }} {{ currentMonthDate.getFullYear() }}
            </h5>
            <button class="btn btn-sm btn-outline-secondary" (click)="nextMonth()" aria-label="Next Month">
              <fa-icon [icon]="faChevronRight"></fa-icon>
            </button>
          </div>
        </div>
        <div class="card-body">
          <div class="calendar-hint text-muted mb-3">
            <i class="fa fa-info-circle"></i> Click on any day to add a new event for that date
          </div>
          <div class="calendar">
            <!-- Day names -->
            <div class="calendar-row day-names">
              <div class="calendar-cell" *ngFor="let day of dayNames">
                {{ day }}
              </div>
            </div>

            <!-- Calendar grid -->
            <div class="calendar-grid">
              <ng-container *ngFor="let day of daysInMonth; let i = index">
                <div class="calendar-cell" [ngClass]="{'empty-cell': day === null}" (click)="day !== null && openAddEventModalWithDate(day)">
                  <ng-container *ngIf="day !== null">
                    <div class="day-number">{{ day }}</div>

                    <!-- Event indicators -->
                    <ng-container *ngIf="getEventsForDay(day) | async as events">
                      <div class="event-indicators" (click)="$event.stopPropagation()">
                        <div class="birthday-indicator" *ngIf="events.birthdays.length > 0"
                             title="{{events.birthdays.length}} Birthday(s)">
                          <i class="fa fa-birthday-cake"></i>
                          <span class="count" *ngIf="events.birthdays.length > 1">{{ events.birthdays.length }}</span>
                        </div>

                        <div class="anniversary-indicator" *ngIf="events.anniversaries.length > 0"
                             title="{{events.anniversaries.length}} Work Anniversary(s)">
                          <i class="fa fa-award"></i>
                          <span class="count" *ngIf="events.anniversaries.length > 1">{{ events.anniversaries.length }}</span>
                        </div>

                        <!-- Birthday details -->
                        <div class="event-details" *ngIf="events.birthdays.length > 0">
                          <div *ngFor="let emp of events.birthdays" class="event-detail-item">
                            <div class="event-name">
                              <strong>{{ emp.name }}'s Birthday</strong>
                              <div class="employee-details">
                                <small>Born: {{ formatDate(emp.date || emp.event_date) }}</small>
                                <small *ngIf="emp.position">Position: {{ emp.position }}</small>
                                <small *ngIf="emp.department">Dept: {{ emp.department }}</small>
                              </div>
                            </div>
                            <div class="event-actions">
                              <button class="btn btn-sm btn-outline-info" (click)="editEventByEmployeeId(emp.employeeId, 'birthday')" title="Edit Birthday">
                                <i class="fa fa-edit"></i>
                              </button>
                              <button class="btn btn-sm btn-outline-danger" (click)="deleteEventByEmployeeId(emp.employeeId, 'birthday')" title="Delete Birthday">
                                <i class="fa fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        </div>

                        <!-- Anniversary details -->
                        <div class="event-details" *ngIf="events.anniversaries.length > 0">
                          <div *ngFor="let emp of events.anniversaries" class="event-detail-item">
                            <div class="event-name">
                              <strong>{{ emp.name }}'s Anniversary</strong>
                              <div class="employee-details">
                                <small>Hired: {{ formatDate(emp.date || emp.event_date) }}</small>
                                <small *ngIf="emp.position">Position: {{ emp.position }}</small>
                                <small *ngIf="emp.department">Dept: {{ emp.department }}</small>
                              </div>
                            </div>
                            <div class="event-actions">
                              <button class="btn btn-sm btn-outline-info" (click)="editEventByEmployeeId(emp.employeeId, 'workAnniversary')" title="Edit Anniversary">
                                <i class="fa fa-edit"></i>
                              </button>
                              <button class="btn btn-sm btn-outline-danger" (click)="deleteEventByEmployeeId(emp.employeeId, 'workAnniversary')" title="Delete Anniversary">
                                <i class="fa fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </ng-container>
                  </ng-container>
                </div>
              </ng-container>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Upcoming Events -->
    <div class="col-md-4">
      <div class="card upcoming-events-card">
        <div class="card-header">
          <div class="d-flex justify-content-between align-items-center">
            <h5>Upcoming Events</h5>
            <button class="btn btn-sm btn-info" (click)="exportUpcomingEvents()">
              <i class="fa fa-file-export"></i> Export Excel
            </button>
          </div>
        </div>
        <div class="card-body">
          <!-- Birthdays -->
          <section class="birthdays-section">
            <h6><i class="fa fa-birthday-cake"></i> Birthdays</h6>
            <ul class="list-group">
              <li class="list-group-item" *ngFor="let event of upcomingBirthdays$ | async">
                <div class="event-info">
                  <div class="employee-name">{{ event.employee.name }}</div>
                  <div class="event-date">
                    {{ formatDate(event.eventDate) }} <span class="badge bg-info">{{ event.label }}</span>
                  </div>
                  <div class="d-flex justify-content-between align-items-center mt-2">
                    <div class="days-until">
                      <span class="badge" [ngClass]="{'bg-primary': event.daysUntil > 7, 'bg-warning': event.daysUntil <= 7, 'bg-danger': event.daysUntil <= 3}">
                        {{ event.daysUntil === 0 ? 'Today' : event.daysUntil === 1 ? 'Tomorrow' : event.daysUntil + ' days' }}
                      </span>
                    </div>
                    <div class="action-buttons">
                      <button class="btn btn-sm btn-info mr-1" (click)="editEvent(event)">Edit</button>
                      <button class="btn btn-sm btn-danger" (click)="deleteEvent(event.id)">Delete</button>
                    </div>
                  </div>
                </div>
              </li>
              <li class="list-group-item text-center" *ngIf="(upcomingBirthdays$ | async)?.length === 0">
                No upcoming birthdays in the next 30 days.
              </li>
            </ul>
          </section>

          <!-- Anniversaries -->
          <section class="anniversaries-section mt-4">
            <h6><i class="fa fa-award"></i> Work Anniversaries</h6>
            <ul class="list-group">
              <li class="list-group-item" *ngFor="let event of upcomingAnniversaries$ | async">
                <div class="event-info">
                  <div class="employee-name">{{ event.employee.name }}</div>
                  <div class="event-date">
                    {{ formatDate(event.eventDate) }} <span class="badge bg-info">{{ event.label }}</span>
                  </div>
                  <div class="d-flex justify-content-between align-items-center mt-2">
                    <div class="days-until">
                      <span class="badge" [ngClass]="{'bg-primary': event.daysUntil > 7, 'bg-warning': event.daysUntil <= 7, 'bg-danger': event.daysUntil <= 3}">
                        {{ event.daysUntil === 0 ? 'Today' : event.daysUntil === 1 ? 'Tomorrow' : event.daysUntil + ' days' }}
                      </span>
                    </div>
                    <div class="action-buttons">
                      <button class="btn btn-sm btn-danger" (click)="deleteEvent(event.id)">Delete</button>
                    </div>
                  </div>
                </div>
              </li>
              <li class="list-group-item text-center" *ngIf="(upcomingAnniversaries$ | async)?.length === 0">
                No upcoming work anniversaries in the next 30 days.
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Event Modal -->
  <div *ngIf="showEventModal" class="modal-container">
    <!-- Backdrop - prevent click events from propagating through modal content to backdrop -->
    <div class="modal-backdrop" (click)="closeEventModal()"></div>
    
    <!-- Modal dialog - stop click propagation to prevent backdrop click handler -->
    <div class="modal-dialog" role="document" (click)="$event.stopPropagation()">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ editingEvent ? 'Edit Event' : 'Add New Event' }}</h5>
          <button type="button" class="close-btn" (click)="closeEventModal()" aria-label="Close">
            Close
          </button>
        </div>
        <div class="modal-body">
          <form [formGroup]="eventForm" (ngSubmit)="saveEvent()">
            <div class="form-group">
              <label for="employeeId">Employee</label>
              <select id="employeeId" class="form-control" formControlName="employeeId" required>
                <option [ngValue]="null">Select Employee</option>
                <option *ngFor="let employee of employees$ | async" [ngValue]="employee.id">
                  {{ employee.name }}
                </option>
              </select>
              <div *ngIf="eventForm.get('employeeId')?.invalid && eventForm.get('employeeId')?.touched" class="text-danger">
                Employee is required.
              </div>
            </div>

            <div class="form-group">
              <label for="eventType">Event Type</label>
              <select id="eventType" class="form-control" formControlName="eventType" required>
                <option value="">Select Event Type</option>
                <option *ngFor="let type of personalEventTypes" [value]="type">
                  {{ type === 'birthday' ? 'Birthday' : 'Work Anniversary' }}
                </option>
              </select>
              <div *ngIf="eventForm.get('eventType')?.invalid && eventForm.get('eventType')?.touched" class="text-danger">
                Event type is required.
              </div>
            </div>

            <div class="form-group">
              <label for="eventDate">Event Date</label>
              <input id="eventDate" type="date" class="form-control" formControlName="eventDate" required>
              <small class="form-text text-muted">
                <i class="fa fa-info-circle"></i> 
                <strong>Important:</strong> For birthdays, enter the person's actual birth date including the correct year. 
                For work anniversaries, enter the actual hire date including the year. 
                This is used to calculate age and years of service.
              </small>
              <div *ngIf="eventForm.get('eventDate')?.invalid && eventForm.get('eventDate')?.touched" class="text-danger">
                Event date is required.
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeEventModal()">Cancel</button>
          <button type="button" class="btn btn-primary" [disabled]="eventForm.invalid" (click)="saveEvent()">
            {{ editingEvent ? 'Update' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
